import * as THREE from "three";
import { splice } from "../shader";
import type { ArtStyle, Surface } from "./types";

// Retro (#76): PS1-era THPS 1/2. The frame is drawn at 240 lines and blown up
// with hard pixel edges (`resolution`), textures are sampled point-style at a
// low resolution, light is flat-shaded facets with no specular or
// reflections, the color is stepped down to 15 bits, and the fog comes in
// close like a short draw distance. Everything happens in each material's own
// shader, so it costs nothing after the scene: no post chain, on Low or High.
//
// #157: the owner liked the pixels but not the flashing, so the PS1's
// artifacts that change a pixel without the scene moving under it are gone:
// the vertex snapping (edges crawled as the camera moved), the affine texture
// warp (textures swam) and the 4x4 ordered dither (it shimmered as colors
// drifted past its thresholds). The 15-bit color now bands plainly.

/** The coarsest a texture is sampled: a 512 texture reads as 64 texels a side. */
const TEXELS = 64;
/** Levels per color channel: 5 bits, the PS1's 15-bit color. */
const LEVELS = 31;

/**
 * The PS1 look in a standard material's shader: low-res point-sampled
 * textures and color stepped down to 15 bits. Exported for the test that
 * checks every anchor it hooks is in three's shader.
 */
export function retroShader(shader: { vertexShader: string; fragmentShader: string }): void {
  shader.fragmentShader = splice(
    shader.fragmentShader,
    "#include <map_fragment>",
    `#ifdef USE_MAP
      {
        vec2 retroFull = vec2(textureSize(map, 0));
        vec2 retroTs = min(retroFull, vec2(${TEXELS.toFixed(1)}));
        vec2 retroUv = (floor(vMapUv * retroTs) + 0.5) / retroTs;
        // The mip level whose texels are the coarse ones, so a coarse texel is
        // the average under it; further off, the level the screen would pick,
        // so a far surface blurs instead of skipping texels and sparkling.
        vec2 retroTexel = vMapUv * retroFull;
        float retroFar = log2(max(max(length(dFdx(retroTexel)), length(dFdy(retroTexel))), 1.0));
        diffuseColor *= textureLod(map, retroUv, max(log2(max(retroFull.x / retroTs.x, 1.0)), retroFar));
      }
      #endif`,
    "replace",
  );
  shader.fragmentShader = splice(
    shader.fragmentShader,
    "#include <dithering_fragment>",
    `{
        // Down to ${LEVELS + 1} levels per channel, rounded: plain bands, no dither.
        const float retroLevels = ${LEVELS.toFixed(1)};
        gl_FragColor.rgb = floor(clamp(gl_FragColor.rgb, 0.0, 1.0) * retroLevels + 0.5) / retroLevels;
      }`,
  );
}

export const retro: ArtStyle = {
  id: "retro",
  name: "Retro",
  pitch: "Late-90s console look: 240 chunky lines, pixel textures, 15-bit color, fog up close.",
  cost: "low",

  // Matte, flat-shaded facets: no specular or reflections on the PS1. Glass
  // keeps a little shine so windows still read.
  material(color, params, surface: Surface) {
    const mat = new THREE.MeshStandardMaterial({
      ...params,
      color,
      roughness: surface === "glass" ? 0.35 : 1,
      metalness: 0,
      flatShading: true,
    });
    mat.onBeforeCompile = retroShader;
    mat.customProgramCacheKey = () => "retro";
    return mat;
  },

  outlines: { scale: 0, ink: 0x000000, edges: 0 },

  lighting: {
    sunColor: 0xffffff,
    sun: 1,
    // Flat, bright ambient like vertex-lit consoles; almost no environment.
    hemi: 1.3,
    environment: "studio",
    envIntensity: { outdoor: 0.1, indoor: 0.1 },
  },

  sky: { clouds: false },
  // The short draw distance: fog starts close and swallows the far end of the park.
  fog: { near: 0.2, far: 0.42 },

  toneMapping: THREE.NoToneMapping,
  exposure: 1,

  post: { ao: null, bloom: null, grade: null, tiltShift: null, mood: null, dof: null },

  dust: null,

  resolution: { lines: 240 },

  // The toy rider's pieces in more grown-up proportions, like the PS1 pros:
  // a smaller head and eyes, a slightly longer chest, and low-poly: about a
  // third of the segments on every round piece, so the flat shading shows
  // facets (a head of 11 x 8 quads, 6-sided limbs, box-cornered shoes).
  rider: {
    head: 0.8,
    eyes: 0.55,
    face: "toy",
    limbs: 0.95,
    chest: { depth: 0.95, height: 1.04 },
    torso: "capsule",
    joints: "ball",
    hands: "mitten",
    shoes: { length: 1, profile: 0.95 },
    feet: "toy",
    facets: 0.35,
  },

  // Hard, blocky shadows.
  shadows: { mapSize: 1024, radius: 1, bias: -0.0008, normalBias: 0.06 },
};
